import {
    Add,
    ClearAll,
    Delete,
    Edit,
    FilterAltOutlined,
} from "@mui/icons-material";
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    LinearProgress,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppContext } from "../../../../Utilities/AppContext";
import { DELETE, GET, PATCH } from "../../../../Utilities/BaseService";
import { MultiPermissionAuthorize } from "../../../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../../../Utilities/UpdatedSearchableInput";

const initialFilter = {
    district: "",
    powerstation: "",
};

const initialClearAll = {
    district: false,
    powerstation: false,
};

const ActiveInactiveReadingCollectionBills = () => {
    const { user } = useContext(AppContext);
    const canActivate = user?.ops?.includes("ACTIVATE DEACTIVATE");

    const { data: usersRes, isLoading: usersLoading } = useQuery(
        ["reading-bill-on-off-users"],
        () => GET("/user", { page: 1, limit: 10000 })
    );

    const users = usersRes?.data?.rows || [];

    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editItem, setEditItem] = useState<any | null>(null);
    const [editValues, setEditValues] = useState({
        readings: false,
        bills: false,
        collections: false,
        users: [] as any[],
    });

    // updatingId can be a number (single row) or "bulk-readings", "bulk-bills", etc.
    const [updatingId, setUpdatingId] = useState<number | string | null>(null);

    const [clearAll, setClearAll] = useState(initialClearAll);
    const [intermediateFilter, setIntermediateFilter] = useState(initialFilter);
    const [filter, setFilter] = useState<any>({});

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [totalRows, setTotalRows] = useState(0);

    // Helper: Checks if all items in the CURRENT visible list are ON for a specific field
    const isAllSelected = (field: "readings" | "bills" | "collections") => {
        if (data.length === 0) return false;
        return data.every((item) => item[field] === true);
    };

    const getReadingOnOff = async (pageNumber = 0, limit = 10, filter: any) => {
        setLoading(true);
        try {
            const query = new URLSearchParams({
                page: String(pageNumber + 1),
                limit: String(limit),
                ...(filter.district && { district: filter.district }),
                ...(filter.powerstation && {
                    powerstation: filter.powerstation,
                }),
            }).toString();

            const response = (await GET(
                `/reading/bill-on-off?${query}`
            )) as any;

            setData(response?.data?.rows || []);
            setTotalRows(response?.data?.count || 0);
        } catch {
            setData([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getReadingOnOff(page, rowsPerPage, filter);
    }, [page, rowsPerPage, filter]);

    // Handle single switch toggle
    const handleToggle = async (
        id: number,
        field: "readings" | "bills" | "collections"
    ) => {
        const item = data.find((d) => d.id === id);
        if (!item) return;

        const updatedValue = !item[field];
        setUpdatingId(id);

        try {
            await PATCH("/reading/bill-on-off", {
                id,
                [field]: updatedValue,
            });

            setData((prev) =>
                prev.map((row) =>
                    row.id === id ? { ...row, [field]: updatedValue } : row
                )
            );
        } finally {
            setUpdatingId(null);
        }
    };

    const getUserLabel = (user: any) =>
        user?.name || user?.username || `${user?.id ?? "Unknown"}`;

    const handleOpenEdit = (item: any) => {
        setEditItem(item);
        setEditValues({
            readings: item.readings,
            bills: item.bills,
            collections: item.collections,
            users:
                item.assignedUsers?.map((assigned: any) => assigned.user) || [],
        });
        setEditDialogOpen(true);
    };

    const handleCloseEdit = () => {
        setEditDialogOpen(false);
        setEditItem(null);
    };

    const handleSaveEdit = async () => {
        if (!editItem) return;

        setUpdatingId(editItem.id);
        try {
            const response = await PATCH("/reading/bill-on-off", {
                id: editItem.id,
                readings: editValues.readings,
                bills: editValues.bills,
                collections: editValues.collections,
                users: editValues.users.map((user) => user.id),
            });

            setData((prev) =>
                prev.map((row) =>
                    row.id === editItem.id
                        ? {
                              ...row,
                              readings: editValues.readings,
                              bills: editValues.bills,
                              collections: editValues.collections,
                              assignedUsers: editValues.users.map((user) => ({
                                  user,
                              })),
                          }
                        : row
                )
            );

            handleCloseEdit();
        } catch (error) {
            console.error("Edit save failed", error);
            alert("Failed to update the bill-on-off record.");
        } finally {
            setUpdatingId(null);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Delete this bill on/off configuration?")) {
            return;
        }

        setUpdatingId(id);
        try {
            await DELETE("/reading/bill-on-off", null, { id });
            setData((prev) => prev.filter((row) => row.id !== id));
        } catch (error) {
            console.error("Delete failed", error);
            alert("Failed to delete the bill-on-off record.");
        } finally {
            setUpdatingId(null);
        }
    };

    // Handle Bulk Toggle via Promise.all
    const handleBulkToggle = async (
        field: "readings" | "bills" | "collections"
    ) => {
        if (data.length === 0) return;

        // If all are currently ON, we turn them all OFF. Otherwise, we turn all ON.
        const targetValue = !isAllSelected(field);

        setUpdatingId(`bulk-${field}`);

        try {
            // Create an array of promises for every item currently in the table
            const updatePromises = data.map((item) =>
                PATCH("/reading/bill-on-off", {
                    id: item.id,
                    [field]: targetValue,
                })
            );

            // Execute all PATCH requests in parallel
            await Promise.all(updatePromises);

            // Update local state for all rows
            setData((prev) =>
                prev.map((row) => ({
                    ...row,
                    [field]: targetValue,
                }))
            );
        } catch (error) {
            console.error("Bulk update failed", error);
            alert("Failed to update some records. Please refresh.");
        } finally {
            setUpdatingId(null);
        }
    };

    const handleClearAll = () => {
        setIntermediateFilter(initialFilter);
        setFilter({});
        setPage(0);
        setClearAll({
            district: true,
            powerstation: true,
        });
        setTimeout(() => setClearAll(initialClearAll), 0);
    };

    // Shared style for the switches (Red when off, Green when on)
    const switchSx = {
        "& .MuiSwitch-switchBase.Mui-checked": { color: "success.main" },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: "success.main",
        },
        "& .MuiSwitch-switchBase": { color: "error.main" },
        "& .MuiSwitch-track": { backgroundColor: "error.main" },
    };

    return (
        <div style={{ padding: 20 }}>
            {/* HEADER */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="h5">
                    Activate / Inactive Readings Bills & Collections
                </Typography>

                <MultiPermissionAuthorize ops={["ACTIVATE DEACTIVATE"]}>
                    <Link
                        to="/bills/readings/bills/collections/active-inactive/create"
                        style={{ textDecoration: "none" }}
                    >
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<Add />}
                        >
                            Add Active Inactive
                        </Button>
                    </Link>
                </MultiPermissionAuthorize>
            </div>

            {/* FILTERS */}
            <Grid container spacing={2} mt={2}>
                <Grid item xs={12} md={3}>
                    <UpdatedSearchableInput
                        label="District"
                        api="/region/district"
                        filter={intermediateFilter}
                        setFilter={setIntermediateFilter}
                        multiple={false}
                        clearAll={clearAll.district}
                    />
                </Grid>

                <Grid item xs={12} md={3}>
                    <UpdatedSearchableInput
                        label="Powerstation"
                        api="/powerstation"
                        filter={intermediateFilter}
                        setFilter={setIntermediateFilter}
                        multiple={false}
                        clearAll={clearAll.powerstation}
                        dep={{ district: intermediateFilter.district }}
                        params={{ district: intermediateFilter.district }}
                    />
                </Grid>

                <Grid item xs={12} md={3}>
                    <Button
                        fullWidth
                        color="secondary"
                        variant="outlined"
                        startIcon={<FilterAltOutlined />}
                        onClick={() => {
                            setPage(0);
                            setFilter({ ...intermediateFilter });
                        }}
                    >
                        Search
                    </Button>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        startIcon={<ClearAll />}
                        onClick={handleClearAll}
                    >
                        Clear All
                    </Button>
                </Grid>
            </Grid>

            {!canActivate && (
                <Typography color="error" sx={{ mt: 2 }}>
                    You do not have permission to change active/inactive
                    statuses.
                </Typography>
            )}

            {loading && <LinearProgress sx={{ mt: 2 }} />}

            {/* TABLE */}
            <Table sx={{ mt: 2 }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>S.No</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                            District
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                            Powerstation
                        </TableCell>
                        {/* <TableCell sx={{ fontWeight: "bold" }}>
                            BillReadingOnOff ID
                        </TableCell> */}
                        <TableCell sx={{ fontWeight: "bold" }}>
                            Assigned Users
                        </TableCell>

                        <TableCell sx={{ fontWeight: "bold" }} align="center">
                            Edit
                        </TableCell>

                        <TableCell sx={{ fontWeight: "bold" }} align="center">
                            DELETE
                        </TableCell>

                        {/* Master Controls */}
                        {["readings", "bills", "collections"].map((field) => (
                            <TableCell key={field} align="center">
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: "bold",
                                            textTransform: "capitalize",
                                        }}
                                    >
                                        {field}
                                    </Typography>
                                    <Switch
                                        size="small"
                                        checked={isAllSelected(field as any)}
                                        onChange={() =>
                                            handleBulkToggle(field as any)
                                        }
                                        disabled={
                                            !canActivate ||
                                            updatingId !== null ||
                                            loading ||
                                            data.length === 0
                                        }
                                        sx={switchSx}
                                    />
                                </Box>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {data.map((item, idx) => (
                        <TableRow key={item.id}>
                            <TableCell>
                                {page * rowsPerPage + idx + 1}
                            </TableCell>
                            <TableCell>{item.district?.name}</TableCell>
                            <TableCell>{item.powerstation?.name}</TableCell>
                            {/* <TableCell>{item.id}</TableCell> */}
                            <TableCell>
                                {item.assignedUsers?.length > 0
                                    ? item.assignedUsers
                                          .map((assigned: any) =>
                                              getUserLabel(assigned.user)
                                          )
                                          .filter(Boolean)
                                          .join(", ")
                                    : "-"}
                            </TableCell>
                            <TableCell align="center">
                                <MultiPermissionAuthorize
                                    ops={["EDIT_ACTIVATE DEACTIVATE"]}
                                    fallback={null}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => handleOpenEdit(item)}
                                        disabled={!canActivate}
                                    >
                                        <Edit fontSize="small" />
                                    </IconButton>
                                </MultiPermissionAuthorize>
                            </TableCell>

                            <TableCell>
                                <MultiPermissionAuthorize
                                    ops={["DELETE_ACTIVATE DEACTIVATE"]}
                                    fallback={null}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDelete(item.id)}
                                        disabled={!canActivate}
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>{" "}
                                </MultiPermissionAuthorize>
                            </TableCell>

                            {["readings", "bills", "collections"].map(
                                (field) => (
                                    <TableCell key={field} align="center">
                                        <Switch
                                            checked={item[field]}
                                            onChange={() =>
                                                handleToggle(
                                                    item.id,
                                                    field as any
                                                )
                                            }
                                            // Disable if this row is updating OR if a bulk operation is running
                                            disabled={
                                                !canActivate ||
                                                updatingId === item.id ||
                                                (typeof updatingId ===
                                                    "string" &&
                                                    updatingId.startsWith(
                                                        "bulk"
                                                    ))
                                            }
                                            sx={switchSx}
                                        />
                                    </TableCell>
                                )
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <TablePagination
                component="div"
                count={totalRows}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
            />

            <Dialog
                open={editDialogOpen}
                onClose={handleCloseEdit}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Edit Bill On/Off</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                options={users}
                                getOptionLabel={getUserLabel}
                                value={editValues.users}
                                onChange={(_, newValue) =>
                                    setEditValues((prev) => ({
                                        ...prev,
                                        users: newValue,
                                    }))
                                }
                                filterSelectedOptions
                                loading={usersLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Assigned Users"
                                        placeholder="Select users"
                                        fullWidth
                                        size="small"
                                    />
                                )}
                                renderTags={(value: any[], getTagProps) =>
                                    value.map((option: any, index: number) => (
                                        <Chip
                                            label={getUserLabel(option)}
                                            {...getTagProps({ index })}
                                            key={option.id}
                                        />
                                    ))
                                }
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEdit}>Cancel</Button>
                    <Button
                        onClick={handleSaveEdit}
                        variant="contained"
                        disabled={updatingId === editItem?.id}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ActiveInactiveReadingCollectionBills;
